<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, apiMessage } from '@/lib/api'
import { fa, jalali, money, statusSeverity } from '@/lib/format'

/**
 * What an operator sees on opening the panel.
 *
 * Deliberately short: today's takings, the queues that need a person, and what is about to run
 * out. A dashboard that shows everything is one nobody reads.
 */
interface Dashboard {
  today: { orders: number; revenue_toman: number; new_customers: number }
  attention: { awaiting_payment: number; payment_failed: number; processing: number; packed: number }
  recent_orders: Array<{
    id: string
    number: string
    status: string
    status_label: string
    total_toman: number
    customer: string | null
    placed_at: string | null
  }>
  low_stock: Array<{ sku: string | null; variant_sku: string; name: string | null; stock_qty: number; threshold: number }>
}

const data = ref<Dashboard | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

/** Each queue links to the list already filtered, so a count is one click from the work. */
const QUEUES: Array<{ key: keyof Dashboard['attention']; label: string; status: string }> = [
  { key: 'awaiting_payment', label: 'در انتظار پرداخت', status: 'pending_payment' },
  { key: 'payment_failed', label: 'پرداخت ناموفق', status: 'payment_failed' },
  { key: 'processing', label: 'در حال آماده‌سازی', status: 'processing' },
  { key: 'packed', label: 'بسته‌بندی‌شده', status: 'packed' },
]

onMounted(async () => {
  try {
    const response = await api.get<{ data: Dashboard }>('/dashboard')
    data.value = response.data.data
  } catch (e) {
    // Without this the screen renders as an empty page and says nothing at all — an operator
    // cannot tell a broken endpoint from a store with no orders yet.
    error.value = apiMessage(e, 'خواندن آمار ممکن نشد.')
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div v-if="loading" class="admin-loading">در حال خواندن آمار…</div>

  <p v-else-if="error" class="admin-error">{{ error }}</p>

  <div v-else-if="data" class="dashboard-grid">
    <section class="stat-row" aria-label="امروز">
      <article class="stat">
        <small>سفارش‌های پرداخت‌شده امروز</small>
        <b>{{ fa(data.today.orders) }}</b>
      </article>
      <article class="stat">
        <small>فروش امروز</small>
        <b>{{ money(data.today.revenue_toman) }}</b>
      </article>
      <article class="stat">
        <small>مشتری تازه امروز</small>
        <b>{{ fa(data.today.new_customers) }}</b>
      </article>
    </section>

    <section class="admin-card" aria-labelledby="queues">
      <h2 id="queues">نیازمند رسیدگی</h2>

      <div class="queue-row">
        <RouterLink
          v-for="queue in QUEUES"
          :key="queue.key"
          class="queue"
          :class="{ 'is-empty': data.attention[queue.key] === 0 }"
          :to="{ path: '/orders', query: { status: queue.status } }"
        >
          <b>{{ fa(data.attention[queue.key]) }}</b>
          <small>{{ queue.label }}</small>
        </RouterLink>
      </div>
    </section>

    <div class="dashboard-columns">
      <section class="admin-card" aria-labelledby="recent">
        <h2 id="recent">آخرین سفارش‌ها</h2>

        <table class="admin-table">
          <thead>
            <tr><th>شماره</th><th>مشتری</th><th>وضعیت</th><th>مبلغ</th><th>تاریخ</th></tr>
          </thead>
          <tbody>
            <tr v-for="order in data.recent_orders" :key="order.id">
              <td><RouterLink :to="`/orders/${order.id}`">{{ order.number }}</RouterLink></td>
              <td>{{ order.customer ?? '—' }}</td>
              <td><span class="badge" :class="`is-${statusSeverity(order.status)}`">{{ order.status_label }}</span></td>
              <td>{{ money(order.total_toman) }}</td>
              <td>{{ jalali(order.placed_at) }}</td>
            </tr>
            <tr v-if="!data.recent_orders.length"><td colspan="5" class="empty">هنوز سفارشی ثبت نشده است.</td></tr>
          </tbody>
        </table>
      </section>

      <section class="admin-card" aria-labelledby="lowStock">
        <h2 id="lowStock">رو به اتمام</h2>

        <table class="admin-table">
          <thead><tr><th>محصول</th><th>موجودی</th><th>حد هشدار</th></tr></thead>
          <tbody>
            <tr v-for="item in data.low_stock" :key="item.variant_sku">
              <td><RouterLink v-if="item.sku" :to="`/products/${item.sku}`">{{ item.name }}</RouterLink></td>
              <td><b class="is-low">{{ fa(item.stock_qty) }}</b></td>
              <td>{{ fa(item.threshold) }}</td>
            </tr>
            <tr v-if="!data.low_stock.length"><td colspan="3" class="empty">موجودی همه محصولات کافی است.</td></tr>
          </tbody>
        </table>
      </section>
    </div>
  </div>
</template>

<style scoped>
.dashboard-grid {
  display: grid;
  gap: 18px;
}

.stat-row {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.stat {
  padding: 20px;
  border: 1px solid var(--line, rgb(110 23 54 / 14%));
  border-radius: 16px;
  background: var(--ivory, #fffefe);
}

.stat small {
  font-size: 12px;
  color: var(--muted, #7a6870);
}

.stat b {
  display: block;
  margin-block-start: 8px;
  font-size: 22px;
  color: var(--plum-deep, #3d0e22);
}

.queue-row {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.queue {
  display: grid;
  gap: 4px;
  padding: 16px;
  border: 1px solid var(--line, rgb(110 23 54 / 14%));
  border-radius: 14px;
  background: var(--paper, #fffbfc);
  text-align: center;
  text-decoration: none;
  color: inherit;
}

.queue b {
  font-size: 20px;
  color: var(--rose-deep, #e8457a);
}

.queue.is-empty b {
  color: var(--muted, #7a6870);
}

.queue small {
  font-size: 11px;
  color: var(--muted, #7a6870);
}

.dashboard-columns {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
}

@media (width <= 1000px) {
  .dashboard-columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

.is-low {
  color: var(--rose-deep, #e8457a);
}
</style>
